import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  IconButton,
  Typography,
  Chip,
  Checkbox,
  FormControlLabel,
  Tooltip,
} from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import { rolesAPI } from '../../services/api';

const emptyForm = { role_name: '', description: '', permission_keys: [] };

const RolesTab = () => {
  const [roles, setRoles] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [blockedUsers, setBlockedUsers] = useState([]);

  useEffect(() => {
    loadRoles();
    loadCatalog();
  }, []);

  const loadRoles = async () => {
    try {
      const response = await rolesAPI.getAll();
      setRoles(response.data);
    } catch {
      setError('Failed to load roles');
    }
  };

  const loadCatalog = async () => {
    try {
      const response = await rolesAPI.getPermissionCatalog();
      setCatalog(response.data);
    } catch {
      setError('Failed to load permission list');
    }
  };

  const groupedCatalog = catalog.reduce((groups, p) => {
    (groups[p.group] = groups[p.group] || []).push(p);
    return groups;
  }, {});

  const openAdd = () => {
    setEditing(null);
    setForm(emptyForm);
    setError('');
    setBlockedUsers([]);
    setOpen(true);
  };

  const openEdit = (role) => {
    setEditing(role);
    setForm({ role_name: role.role_name, description: role.description || '', permission_keys: [...role.permission_keys] });
    setError('');
    setBlockedUsers([]);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditing(null);
    setForm(emptyForm);
    setError('');
  };

  const togglePermission = (key) => {
    setForm((prev) => ({
      ...prev,
      permission_keys: prev.permission_keys.includes(key)
        ? prev.permission_keys.filter((k) => k !== key)
        : [...prev.permission_keys, key],
    }));
  };

  const handleSubmit = async () => {
    if (!form.role_name.trim()) {
      setError('Role name is required');
      return;
    }
    try {
      if (editing) {
        await rolesAPI.update(editing.role_id, {
          role_name: form.role_name.trim(),
          description: form.description.trim(),
          permission_keys: form.permission_keys,
        });
        setMessage('Role updated successfully');
      } else {
        await rolesAPI.create({
          role_name: form.role_name.trim(),
          description: form.description.trim(),
          permission_keys: form.permission_keys,
        });
        setMessage('Role created successfully');
      }
      handleClose();
      loadRoles();
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail?.message || err.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDelete = async (role) => {
    if (!window.confirm(`Delete role "${role.role_name}"?`)) return;
    setBlockedUsers([]);
    setError('');
    try {
      await rolesAPI.delete(role.role_id);
      setMessage('Role deleted successfully');
      loadRoles();
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.users) {
        setError(detail.message);
        setBlockedUsers(detail.users);
      } else {
        setError(detail || 'Failed to delete role');
      }
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, alignItems: 'center' }}>
        <Typography variant="h6">Roles</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={openAdd}>
          Add Role
        </Button>
      </Box>

      {message && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage('')}>{message}</Alert>}
      {error && !open && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => { setError(''); setBlockedUsers([]); }}>
          {error}
          {blockedUsers.length > 0 && (
            <Box component="span" sx={{ display: 'block', mt: 0.5 }}>
              {blockedUsers.join(', ')}
            </Box>
          )}
        </Alert>
      )}

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell><strong>Role Name</strong></TableCell>
              <TableCell><strong>Description</strong></TableCell>
              <TableCell><strong>Permissions</strong></TableCell>
              <TableCell><strong>Users</strong></TableCell>
              <TableCell><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {roles.map((role) => (
              <TableRow key={role.role_id} hover>
                <TableCell>
                  {role.role_name}
                  {role.is_system_role && <Chip label="System" size="small" color="secondary" sx={{ ml: 1 }} />}
                </TableCell>
                <TableCell>{role.description || '—'}</TableCell>
                <TableCell>
                  {role.is_system_role ? 'All (bypasses checks)' : `${role.permission_keys.length} granted`}
                </TableCell>
                <TableCell>{role.user_count}</TableCell>
                <TableCell>
                  <Tooltip title={role.is_system_role ? "The Admin role can't be edited - it's always full access" : ''}>
                    <span>
                      <IconButton size="small" disabled={role.is_system_role} onClick={() => openEdit(role)}>
                        <Edit fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title={role.is_system_role ? "The Admin role can't be deleted" : (role.user_count > 0 ? 'Reassign its users first' : '')}>
                    <span>
                      <IconButton
                        size="small"
                        color="error"
                        disabled={role.is_system_role || role.user_count > 0}
                        onClick={() => handleDelete(role)}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Role' : 'Add Role'}</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
          <TextField
            fullWidth
            label="Role Name"
            value={form.role_name}
            onChange={(e) => setForm({ ...form, role_name: e.target.value })}
            sx={{ mt: 1, mb: 2 }}
          />
          <TextField
            fullWidth
            label="Description"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            sx={{ mb: 2 }}
          />

          <Typography variant="subtitle2" gutterBottom>Tab Access</Typography>
          {Object.entries(groupedCatalog).map(([group, perms]) => (
            <Box key={group} sx={{ mb: 1.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                {group}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
                {perms.map((p) => (
                  <FormControlLabel
                    key={p.key}
                    sx={{ width: '50%', mr: 0 }}
                    control={
                      <Checkbox
                        size="small"
                        checked={form.permission_keys.includes(p.key)}
                        onChange={() => togglePermission(p.key)}
                      />
                    }
                    label={p.label}
                  />
                ))}
              </Box>
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">{editing ? 'Update' : 'Add'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RolesTab;
